import { Injectable } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  docData,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  Timestamp,
  writeBatch,
} from '@angular/fire/firestore';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { toRawStockCode, toStockDocId } from '../../../shared/shared-utils/firestore/firestore.utils';
import {
  FIRESTORE_TARGET_DOC_BYTES,
  planChunkWrites,
} from '../utils/stock-take-chunked-storage.utils';
import {
  IStockTakeFinalisedChunkDoc,
  IStockTakeFinalisedMeta,
  IStockTakeFinalFigureStored,
} from '../models/stock-take-finalised-chunk.interface';
import { IStockTakeFinalFigure } from '../models/stock-take-item.interface';
import { STOCK_TAKE_FINALISED_STORAGE_SCHEMA } from '../utils/stock-take-finalised-storage.constants';

const FINALISED_META_DOC_ID = '_meta';

const finalisedMetaPath = (stockTakePath: string): string =>
  `${stockTakePath}/finalised_meta/${FINALISED_META_DOC_ID}`;

const finalisedChunkPath = (stockTakePath: string, chunkId: string): string =>
  `${stockTakePath}/finalised_chunks/${chunkId}`;

const finalisedChunksCollectionPath = (stockTakePath: string): string =>
  `${stockTakePath}/finalised_chunks`;

const FIRESTORE_BATCH_OP_LIMIT = 450;

/**
 * Chunked Firestore storage for stock take finalised figures (report + backend processing).
 */
@Injectable({
  providedIn: 'root',
})
export class StockTakeFinalisedChunkService {
  constructor(private readonly firestore: Firestore) {}

  /**
   * Write all final figures into chunked storage and meta (replaces any prior chunked finalised data).
   */
  async writeFinalFigures(
    stockTakePath: string,
    figures: Omit<IStockTakeFinalFigure, 'id'>[],
  ): Promise<number> {
    await this.deleteAllFinalisedChunks(stockTakePath);

    if (figures.length === 0) {
      return 0;
    }

    const entryList = figures.map((figure) => {
      const rawCode = toRawStockCode(figure.code);
      return {
        key: toStockDocId(figure.code),
        value: { ...figure, code: rawCode } as IStockTakeFinalFigureStored,
      };
    });
    const plans = planChunkWrites(entryList, FIRESTORE_TARGET_DOC_BYTES, 'finalised');
    const chunkDocIds = plans.map((plan) => plan.chunkId);
    const codeToChunkId: Record<string, string> = {};
    for (const plan of plans) {
      for (const key of Object.keys(plan.entries)) {
        codeToChunkId[key] = plan.chunkId;
      }
    }

    const chunkRefs = plans.map((plan) => ({
      ref: doc(this.firestore, finalisedChunkPath(stockTakePath, plan.chunkId)),
      data: { schemaVersion: STOCK_TAKE_FINALISED_STORAGE_SCHEMA, entries: plan.entries },
    }));
    const metaRef = doc(this.firestore, finalisedMetaPath(stockTakePath));
    const meta: IStockTakeFinalisedMeta = {
      schemaVersion: STOCK_TAKE_FINALISED_STORAGE_SCHEMA,
      itemCount: figures.length,
      chunkDocIds,
      codeToChunkId,
      finalisedAt: Timestamp.now(),
    };

    for (let i = 0; i < chunkRefs.length; i += FIRESTORE_BATCH_OP_LIMIT) {
      const batch = writeBatch(this.firestore);
      for (const { ref, data } of chunkRefs.slice(i, i + FIRESTORE_BATCH_OP_LIMIT)) {
        batch.set(ref, data);
      }
      await batch.commit();
    }

    const metaBatch = writeBatch(this.firestore);
    metaBatch.set(metaRef, meta);
    await metaBatch.commit();

    return figures.length;
  }

  /**
   * Real-time stream of finalised figures (chunked layout).
   */
  watchFinalFigures(stockTakePath: string): Observable<IStockTakeFinalFigure[]> {
    const metaRef = doc(this.firestore, finalisedMetaPath(stockTakePath));
    return docData(metaRef).pipe(
      switchMap((metaRaw: DocumentData | undefined) => {
        const meta = metaRaw as IStockTakeFinalisedMeta | undefined;
        if (!meta?.chunkDocIds?.length) {
          return of([] as IStockTakeFinalFigure[]);
        }
        const chunkStreams = meta.chunkDocIds.map((chunkId: string) =>
          docData(doc(this.firestore, finalisedChunkPath(stockTakePath, chunkId))).pipe(
            map((chunkRaw: DocumentData | undefined) => {
              const chunk = chunkRaw as IStockTakeFinalisedChunkDoc | undefined;
              return chunk?.entries ?? {};
            }),
          ),
        );
        return combineLatest(chunkStreams).pipe(
          map((entryMaps: Record<string, IStockTakeFinalFigureStored>[]) =>
            this.flattenEntries(entryMaps),
          ),
        );
      }),
    );
  }

  /**
   * Load finalised figures once from chunked storage.
   */
  async loadFinalFigures(stockTakePath: string): Promise<IStockTakeFinalFigure[]> {
    const meta = await this.getMeta(stockTakePath);
    if (!meta?.chunkDocIds?.length) {
      return [];
    }
    const entryMaps: Record<string, IStockTakeFinalFigureStored>[] = [];
    for (const chunkId of meta.chunkDocIds) {
      const snap = await getDoc(doc(this.firestore, finalisedChunkPath(stockTakePath, chunkId)));
      if (snap.exists()) {
        const chunk = snap.data() as IStockTakeFinalisedChunkDoc;
        entryMaps.push(chunk.entries ?? {});
      }
    }
    return this.flattenEntries(entryMaps);
  }

  /**
   * Whether finalised chunk storage exists for this stock take.
   */
  async hasFinalFigures(stockTakePath: string): Promise<boolean> {
    const meta = await this.getMeta(stockTakePath);
    return !!meta && meta.itemCount > 0;
  }

  /**
   * Delete all finalised chunk documents and meta.
   */
  async deleteAllFinalisedChunks(stockTakePath: string): Promise<void> {
    const meta = await this.getMeta(stockTakePath);
    const chunkCol = collection(this.firestore, finalisedChunksCollectionPath(stockTakePath));
    const legacySnap = await getDocs(chunkCol);
    const refs = legacySnap.docs.map((d) => d.ref);
    if (meta?.chunkDocIds) {
      for (const chunkId of meta.chunkDocIds) {
        refs.push(doc(this.firestore, finalisedChunkPath(stockTakePath, chunkId)));
      }
    }
    for (let i = 0; i < refs.length; i += FIRESTORE_BATCH_OP_LIMIT) {
      const batch = writeBatch(this.firestore);
      for (const ref of refs.slice(i, i + FIRESTORE_BATCH_OP_LIMIT)) {
        batch.delete(ref);
      }
      await batch.commit();
    }
    await deleteDoc(doc(this.firestore, finalisedMetaPath(stockTakePath))).catch(() => undefined);
  }

  private async getMeta(stockTakePath: string): Promise<IStockTakeFinalisedMeta | null> {
    const snap = await getDoc(doc(this.firestore, finalisedMetaPath(stockTakePath)));
    if (!snap.exists()) {
      return null;
    }
    return snap.data() as IStockTakeFinalisedMeta;
  }

  private flattenEntries(
    entryMaps: Record<string, IStockTakeFinalFigureStored>[],
  ): IStockTakeFinalFigure[] {
    const figuresByCode = new Map<string, IStockTakeFinalFigure>();
    for (const map of entryMaps) {
      for (const [encoded, stored] of Object.entries(map)) {
        const code = toRawStockCode(encoded);
        const figure = {
          ...stored,
          id: code,
          code,
        };
        const existing = figuresByCode.get(code);
        figuresByCode.set(code, existing ? this.selectDuplicateDisplayFigure(existing, figure) : figure);
      }
    }
    return Array.from(figuresByCode.values());
  }

  private selectDuplicateDisplayFigure(
    existing: IStockTakeFinalFigure,
    next: IStockTakeFinalFigure,
  ): IStockTakeFinalFigure {
    if ((next.timesCounted ?? 0) > (existing.timesCounted ?? 0)) {
      return next;
    }
    if ((next.qtyCounted ?? 0) !== 0 && (existing.qtyCounted ?? 0) === 0) {
      return next;
    }
    return existing;
  }
}
